import { z } from 'zod';
import { sitePagePathSchema } from '../site/schema';

const siteWorkspaceFilePathSchema = z
  .string()
  .min(1)
  .regex(
    /^(?!\/)(?!.*\/\/)(?!.*(?:^|\/)\.\.(?:\/|$))[A-Za-z0-9._/-]+$/u,
    'workspace file path must be relative and must not contain "..".',
  );

export const siteWorkspaceFrameworkSchema = z.enum(['react_vite']);
export const siteWorkspaceModeSchema = z.enum(['document_bridge', 'code_first']);
export const siteWorkspaceFileLanguageSchema = z.enum(['ts', 'tsx', 'json', 'md']);

export const siteWorkspaceFileSchema = z.object({
  path: siteWorkspaceFilePathSchema,
  language: siteWorkspaceFileLanguageSchema,
  content: z.string(),
  purpose: z.string().optional().or(z.literal('')).default(''),
});

export type SiteWorkspaceFile = z.infer<typeof siteWorkspaceFileSchema>;

export const siteWorkspaceRouteSchema = z.object({
  pageId: z.string().min(1),
  title: z.string().min(1),
  path: sitePagePathSchema,
  entryFilePath: siteWorkspaceFilePathSchema,
});

export type SiteWorkspaceRoute = z.infer<typeof siteWorkspaceRouteSchema>;

export const siteWorkspaceComponentSchema = z.object({
  pageId: z.string().min(1),
  sectionId: z.string().min(1),
  sectionType: z.string().min(1),
  label: z.string().min(1),
  filePath: siteWorkspaceFilePathSchema,
  aliases: z.array(z.string().min(1)).default([]),
});

export type SiteWorkspaceComponent = z.infer<typeof siteWorkspaceComponentSchema>;

export const siteWorkspaceTargetKindSchema = z.enum([
  'route',
  'section-template',
  'section-instance',
  'helper',
  'entry-file',
]);

export type SiteWorkspaceTargetKind = z.infer<typeof siteWorkspaceTargetKindSchema>;

export const siteWorkspaceTargetSchema = z.object({
  path: siteWorkspaceFilePathSchema,
  kind: siteWorkspaceTargetKindSchema,
  label: z.string().min(1),
  aliases: z.array(z.string().min(1)).default([]),
  pageId: z.string().min(1).optional(),
  sectionId: z.string().min(1).optional(),
});

export type SiteWorkspaceTarget = z.infer<typeof siteWorkspaceTargetSchema>;

export const siteWorkspaceSchema = z
  .object({
    version: z.number().int().positive(),
    framework: siteWorkspaceFrameworkSchema.default('react_vite'),
    mode: siteWorkspaceModeSchema.default('document_bridge'),
    entryFilePath: siteWorkspaceFilePathSchema,
    editableFilePaths: z.array(siteWorkspaceFilePathSchema).default([]),
    aiEditableFilePaths: z.array(siteWorkspaceFilePathSchema).default([]),
    routes: z.array(siteWorkspaceRouteSchema).min(1),
    components: z.array(siteWorkspaceComponentSchema).default([]),
    targets: z.array(siteWorkspaceTargetSchema).default([]),
    files: z.array(siteWorkspaceFileSchema).min(1),
    generatedFromDocumentVersion: z.number().int().positive().optional(),
    updatedAt: z.string().optional(),
  })
  .superRefine((value, context) => {
    const filePaths = new Set<string>();

    value.files.forEach((file, index) => {
      if (filePaths.has(file.path)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate workspace file path: ${file.path}`,
          path: ['files', index, 'path'],
        });
      }
      filePaths.add(file.path);
    });

    if (!filePaths.has(value.entryFilePath)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'entryFilePath must exist in files.',
        path: ['entryFilePath'],
      });
    }

    value.editableFilePaths.forEach((path, index) => {
      if (!filePaths.has(path)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'editableFilePaths must reference an existing file.',
          path: ['editableFilePaths', index],
        });
      }
    });

    value.aiEditableFilePaths.forEach((path, index) => {
      if (!filePaths.has(path)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'aiEditableFilePaths must reference an existing file.',
          path: ['aiEditableFilePaths', index],
        });
      }
    });

    value.routes.forEach((route, index) => {
      if (!filePaths.has(route.entryFilePath)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'route entryFilePath must reference an existing file.',
          path: ['routes', index, 'entryFilePath'],
        });
      }
    });

    value.components.forEach((component, index) => {
      if (!filePaths.has(component.filePath)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'component filePath must reference an existing file.',
          path: ['components', index, 'filePath'],
        });
      }
    });

    value.targets.forEach((target, index) => {
      if (!filePaths.has(target.path)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'targets path must reference an existing file.',
          path: ['targets', index, 'path'],
        });
      }
    });
  });

export type SiteWorkspace = z.infer<typeof siteWorkspaceSchema>;
export type SiteWorkspaceFramework = z.infer<typeof siteWorkspaceFrameworkSchema>;
export type SiteWorkspaceMode = z.infer<typeof siteWorkspaceModeSchema>;

export const siteWorkspaceRevisionSchema = z.object({
  id: z.string(),
  site_id: z.string(),
  version: z.number().int().positive(),
  summary: z.string().min(1),
  created_at: z.string(),
});

export type SiteWorkspaceRevision = z.infer<typeof siteWorkspaceRevisionSchema>;
